import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useState, useEffect, useCallback, useRef } from 'react';
import '@/App.css';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import QRLogin from './pages/QRLogin';
import QRApprove from './pages/QRApprove';
import AdminDashboard from './pages/AdminDashboard';
import VehiclesManagement from './pages/VehiclesManagement';
import DeliveriesList from './pages/DeliveriesList';
import DeliveryDetail from './pages/DeliveryDetail';
import PalletStatement from './pages/PalletStatement';
import PalletMonitoring from './pages/PalletMonitoring';
import UserManagement from './pages/UserManagement';
import SupervisorFlow from './pages/SupervisorFlow';
import DriverView from './pages/DriverView';
import Notifications from './pages/Notifications';
import BackupsManagement from './pages/BackupsManagement';
import RouteOpsLayout from './pages/routeops/RouteOpsLayout';
import WeeklyPlanner from './pages/routeops/WeeklyPlanner';
import RouteOpsBookings from './pages/routeops/Bookings';
import RouteOpsTerminal from './pages/routeops/Terminal';
import RouteOpsRouteConfig from './pages/routeops/RouteConfig';
import RouteOpsAuditTrail from './pages/routeops/AuditTrail';
import RouteOpsAIAssistant from './pages/routeops/AIAssistant';
import RouteOpsSettings from './pages/routeops/Settings';
import RouteOpsReporting from './pages/routeops/Reporting';
import RouteOpsChangelog from './pages/routeops/Changelog';
import RouteOpsDriverTracking from './pages/routeops/DriverTracking';
import MaintenanceReminderModal from './components/MaintenanceReminderModal';
import UpdatePopup from './components/UpdatePopup';
import HotfixUpdatePopup from './components/HotfixUpdatePopup';
import DriverLocationTracker from './components/DriverLocationTracker';
import { Toaster, toast } from 'sonner';
import api from './utils/api';

// Default fallback timeouts (overridden per-user from user.session_timeout_seconds)
const DEFAULT_SESSION_TIMEOUT_MS = 120 * 1000;
const HEARTBEAT_INTERVAL_MS = 30 * 1000; // Send heartbeat every 30 seconds

// Role-default module access used when a user does not have an explicit allowed_modules list.
// Must stay in sync with backend ROLE_DEFAULT_MODULES in /app/backend/routes/users.py.
const MAIN_MODULES = ['dashboard', 'new_delivery', 'vehicles', 'deliveries', 'pallet_statement', 'pallet_monitoring', 'notifications', 'routeops', 'user_management', 'backups'];
const ROUTEOPS_MODULES = ['routeops_planner', 'routeops_bookings', 'routeops_terminal', 'routeops_routes', 'routeops_reporting', 'routeops_audit', 'routeops_ai', 'routeops_changelog', 'routeops_driver_tracking', 'routeops_settings'];
const ROLE_DEFAULT_MODULES = {
  Admin: [...MAIN_MODULES, ...ROUTEOPS_MODULES],
  Manager: ['dashboard', 'new_delivery', 'vehicles', 'deliveries', 'pallet_statement', 'notifications', 'routeops', ...ROUTEOPS_MODULES],
  Supervisor: ['new_delivery', 'deliveries', 'pallet_monitoring', 'notifications'],
  Driver: ['deliveries', 'pallet_monitoring'],
};

export const getAllowedModules = (u) => {
  if (!u) return [];
  if (Array.isArray(u.allowed_modules)) return u.allowed_modules;
  return ROLE_DEFAULT_MODULES[u.role] || [];
};

// Helper: check module access. Admin with null => full access via role defaults.
const hasModule = (u, key) => {
  return getAllowedModules(u).includes(key);
};

// Helper: check edit permission. Defaults to 'edit' when permission not explicitly set.
// Returns false if user does not have access to the module at all.
export const canEditModule = (u, key) => {
  if (!hasModule(u, key)) return false;
  const perms = (u && u.module_permissions) || {};
  // Default: edit (for backwards-compat and uninitialised permissions)
  return (perms[key] || 'edit') === 'edit';
};

// Small fallback page shown when the user is logged in but has no permitted route
// (e.g. Admin whose allowed_modules was set to an empty list). Prevents infinite
// redirects between unmatched fallback targets and the catch-all.
const NoAccess = ({ onLogout }) => (
  <div className="min-h-screen flex items-center justify-center p-6" style={{ background: 'var(--bg-void)' }}>
    <div className="max-w-md text-center space-y-4" data-testid="no-access-page">
      <div className="text-5xl">🔒</div>
      <h1 className="text-xl font-bold" style={{ color: 'var(--text-primary)', fontFamily: 'var(--fd)' }}>No Pages Accessible</h1>
      <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
        Your account doesn't have access to any pages yet. Please ask an Admin to grant module access.
      </p>
      {onLogout && (
        <button
          onClick={onLogout}
          className="px-6 py-2 rounded-lg font-bold uppercase text-sm"
          style={{ background: 'var(--amber)', color: 'var(--bg-void)', fontFamily: 'var(--fd)' }}
          data-testid="no-access-logout-btn"
        >
          Logout
        </button>
      )}
    </div>
  </div>
);

// Helper: pick a safe landing path that is guaranteed to render for the user.
const safeLandingPath = (u) => {
  if (!u) return '/login';
  const mods = getAllowedModules(u);
  if (mods.includes('dashboard')) return '/';
  if (mods.includes('routeops')) return '/routeops';
  if (mods.includes('deliveries')) return '/deliveries';
  if (mods.includes('notifications')) return '/notifications';
  if (mods.includes('new_delivery')) return '/new-delivery';
  return '/no-access';
};

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const lastActivityRef = useRef(Date.now());
  const timeoutRef = useRef(null);
  const heartbeatRef = useRef(null);

  // Apply saved theme on mount
  useEffect(() => {
    const theme = localStorage.getItem('fleet-theme') || 'dark';
    if (theme === 'light') {
      document.documentElement.classList.add('theme-light');
    } else {
      document.documentElement.classList.remove('theme-light');
    }
  }, []);

  // Update last activity timestamp
  const updateActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
  }, []);

  // Send heartbeat to server
  const sendHeartbeat = useCallback(async () => {
    if (!user) return;
    
    try {
      await api.post('/auth/heartbeat');
    } catch (error) {
      console.error('Heartbeat failed:', error);
    }
  }, [user]);

  // Check session validity
  const checkSession = useCallback(async () => {
    if (!user) return;
    
    try {
      const response = await api.get('/auth/check-session');
      
      if (!response.data.valid) {
        const reason = response.data.reason;
        
        if (reason === 'session_invalidated') {
          toast.error('You have been logged out from another device');
        } else if (reason === 'session_timeout') {
          toast.error('Session expired due to inactivity');
        } else {
          toast.error('Session expired');
        }
        
        handleLogout(true); // Silent logout
      }
    } catch (error) {
      // If check fails, don't logout (might be network issue)
      console.error('Session check failed:', error);
    }
  }, [user]);

  // Check for inactivity timeout (client-side). Uses per-user setting when available.
  const checkInactivityTimeout = useCallback(() => {
    if (!user) return;
    
    const userTimeoutMs = (user.session_timeout_seconds && user.session_timeout_seconds > 0)
      ? user.session_timeout_seconds * 1000
      : DEFAULT_SESSION_TIMEOUT_MS;
    const timeSinceActivity = Date.now() - lastActivityRef.current;
    
    if (timeSinceActivity >= userTimeoutMs) {
      toast.error('Session expired due to inactivity');
      handleLogout(true);
    }
  }, [user]);

  const handleLogin = (userData, token) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    lastActivityRef.current = Date.now();
  };

  const handleLogout = async (silent = false) => {
    try {
      // Call backend logout endpoint (triggers cleanup for test users)
      await api.post('/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
      // Continue with logout even if backend call fails
    } finally {
      // Clear local storage and state
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('session_id');
      setUser(null);
      
      // Clear timers
      if (timeoutRef.current) clearInterval(timeoutRef.current);
      if (heartbeatRef.current) clearInterval(heartbeatRef.current);
      
      if (!silent) {
        toast.success('Logged out successfully');
      }
    }
  };

  // Setup activity listeners and timers
  useEffect(() => {
    if (!user) return;

    // Activity event listeners
    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];
    
    events.forEach(event => {
      document.addEventListener(event, updateActivity, { passive: true });
    });

    // Start heartbeat interval
    heartbeatRef.current = setInterval(() => {
      sendHeartbeat();
      checkSession();
    }, HEARTBEAT_INTERVAL_MS);

    // Start inactivity check interval
    timeoutRef.current = setInterval(() => {
      checkInactivityTimeout();
    }, 10000); // Check every 10 seconds

    // Cleanup
    return () => {
      events.forEach(event => {
        document.removeEventListener(event, updateActivity);
      });
      
      if (heartbeatRef.current) clearInterval(heartbeatRef.current);
      if (timeoutRef.current) clearInterval(timeoutRef.current);
    };
  }, [user, updateActivity, sendHeartbeat, checkSession, checkInactivityTimeout]);

  useEffect(() => {
    // Check if user is logged in
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    if (token && userData) {
      const cached = JSON.parse(userData);
      setUser(cached);
      // Refresh user from /auth/me in background so allowed_modules/session_timeout stay fresh.
      // Only replace state when something meaningful actually changed to avoid redundant renders
      // that can otherwise trigger fallback Navigates (causing Firefox pushState "insecure" errors).
      api.get('/auth/me').then(res => {
        if (res.data) {
          const fresh = res.data;
          const cachedJson = JSON.stringify(cached);
          const freshJson = JSON.stringify(fresh);
          if (cachedJson !== freshJson) {
            setUser(fresh);
            localStorage.setItem('user', freshJson);
          }
        }
      }).catch(() => {
        // ignore - cached user will be used until next login
      });
    }
    setLoading(false);
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--bg-void)' }}>
        <div className="text-xl font-medium" style={{ color: 'var(--text-secondary)', fontFamily: 'var(--fd)', letterSpacing: '0.05em' }}>Loading...</div>
      </div>
    );
  }

  return (
    <div className="App">
      <Toaster position="top-right" richColors />
      {user && <MaintenanceReminderModal user={user} />}
      {user && <UpdatePopup user={user} />}
      {user && <HotfixUpdatePopup user={user} />}
      {user && <DriverLocationTracker user={user} />}
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={!user ? <Login onLogin={handleLogin} /> : <Navigate to="/" replace />} />
          <Route path="/register" element={!user ? <Register onLogin={handleLogin} /> : <Navigate to="/" replace />} />
          <Route path="/forgot-password" element={!user ? <ForgotPassword /> : <Navigate to="/" replace />} />
          <Route path="/qr-login" element={<QRLogin onLogin={handleLogin} />} />
          <Route path="/qr-approve" element={<QRApprove />} />
          <Route path="/no-access" element={user ? <NoAccess onLogout={handleLogout} /> : <Navigate to="/login" replace />} />
          
          {user && user.role === 'Admin' && (
            <>
              {hasModule(user, 'dashboard') && <Route path="/" element={<AdminDashboard user={user} onLogout={handleLogout} />} />}
              {hasModule(user, 'dashboard') && <Route path="/dashboard" element={<AdminDashboard user={user} onLogout={handleLogout} />} />}
              {!hasModule(user, 'dashboard') && <Route path="/" element={<Navigate to={safeLandingPath(user)} replace />} />}
              {hasModule(user, 'new_delivery') && <Route path="/new-delivery" element={<SupervisorFlow user={user} onLogout={handleLogout} />} />}
              {hasModule(user, 'vehicles') && <Route path="/vehicles" element={<VehiclesManagement user={user} onLogout={handleLogout} />} />}
              {hasModule(user, 'deliveries') && <Route path="/deliveries" element={<DeliveriesList user={user} onLogout={handleLogout} />} />}
              {hasModule(user, 'deliveries') && <Route path="/deliveries/:id" element={<DeliveryDetail user={user} onLogout={handleLogout} />} />}
              {hasModule(user, 'pallet_statement') && <Route path="/pallet-statement" element={<PalletStatement user={user} onLogout={handleLogout} />} />}
              {hasModule(user, 'pallet_monitoring') && <Route path="/pallet-monitoring" element={<PalletMonitoring user={user} onLogout={handleLogout} />} />}
              {hasModule(user, 'notifications') && <Route path="/notifications" element={<Notifications user={user} onLogout={handleLogout} />} />}
              {hasModule(user, 'user_management') && <Route path="/user-management" element={<UserManagement user={user} onLogout={handleLogout} />} />}
              {hasModule(user, 'backups') && <Route path="/backups" element={<BackupsManagement user={user} onLogout={handleLogout} />} />}
              {hasModule(user, 'routeops') && (
                <Route path="/routeops" element={<RouteOpsLayout user={user} onLogout={handleLogout} />}>
                  {hasModule(user, 'routeops_planner') && <Route index element={<WeeklyPlanner />} />}
                  {hasModule(user, 'routeops_bookings') && <Route path="bookings" element={<RouteOpsBookings />} />}
                  {hasModule(user, 'routeops_terminal') && <Route path="terminal" element={<RouteOpsTerminal />} />}
                  {hasModule(user, 'routeops_routes') && <Route path="routes" element={<RouteOpsRouteConfig />} />}
                  {hasModule(user, 'routeops_reporting') && <Route path="reporting" element={<RouteOpsReporting />} />}
                  {hasModule(user, 'routeops_audit') && <Route path="audit" element={<RouteOpsAuditTrail />} />}
                  {hasModule(user, 'routeops_ai') && <Route path="ai" element={<RouteOpsAIAssistant />} />}
                  {hasModule(user, 'routeops_changelog') && <Route path="changelog" element={<RouteOpsChangelog />} />}
                  {hasModule(user, 'routeops_driver_tracking') && <Route path="driver-tracking" element={<RouteOpsDriverTracking />} />}
                  {hasModule(user, 'routeops_settings') && <Route path="settings" element={<RouteOpsSettings />} />}
                </Route>
              )}
            </>
          )}

          {user && user.role === 'Manager' && (
            <>
              {hasModule(user, 'dashboard') && <Route path="/" element={<AdminDashboard user={user} onLogout={handleLogout} />} />}
              {!hasModule(user, 'dashboard') && <Route path="/" element={<Navigate to={safeLandingPath(user)} replace />} />}
              {hasModule(user, 'new_delivery') && <Route path="/new-delivery" element={<SupervisorFlow user={user} onLogout={handleLogout} />} />}
              {hasModule(user, 'vehicles') && <Route path="/vehicles" element={<VehiclesManagement user={user} onLogout={handleLogout} />} />}
              {hasModule(user, 'deliveries') && <Route path="/deliveries" element={<DeliveriesList user={user} onLogout={handleLogout} />} />}
              {hasModule(user, 'deliveries') && <Route path="/deliveries/:id" element={<DeliveryDetail user={user} onLogout={handleLogout} />} />}
              {hasModule(user, 'pallet_statement') && <Route path="/pallet-statement" element={<PalletStatement user={user} onLogout={handleLogout} />} />}
              {hasModule(user, 'notifications') && <Route path="/notifications" element={<Notifications user={user} onLogout={handleLogout} />} />}
              {hasModule(user, 'routeops') && (
                <Route path="/routeops" element={<RouteOpsLayout user={user} onLogout={handleLogout} />}>
                  {hasModule(user, 'routeops_planner') && <Route index element={<WeeklyPlanner />} />}
                  {hasModule(user, 'routeops_bookings') && <Route path="bookings" element={<RouteOpsBookings />} />}
                  {hasModule(user, 'routeops_terminal') && <Route path="terminal" element={<RouteOpsTerminal />} />}
                  {hasModule(user, 'routeops_routes') && <Route path="routes" element={<RouteOpsRouteConfig />} />}
                  {hasModule(user, 'routeops_reporting') && <Route path="reporting" element={<RouteOpsReporting />} />}
                  {hasModule(user, 'routeops_audit') && <Route path="audit" element={<RouteOpsAuditTrail />} />}
                  {hasModule(user, 'routeops_ai') && <Route path="ai" element={<RouteOpsAIAssistant />} />}
                  {hasModule(user, 'routeops_changelog') && <Route path="changelog" element={<RouteOpsChangelog />} />}
                  {hasModule(user, 'routeops_driver_tracking') && <Route path="driver-tracking" element={<RouteOpsDriverTracking />} />}
                  {hasModule(user, 'routeops_settings') && <Route path="settings" element={<RouteOpsSettings />} />}
                </Route>
              )}
            </>
          )}
          
          {user && user.role === 'Supervisor' && (
            <>
              <Route path="/" element={<SupervisorFlow user={user} onLogout={handleLogout} />} />
              <Route path="/notifications" element={<Notifications user={user} onLogout={handleLogout} />} />
              <Route path="/deliveries" element={<DeliveriesList user={user} onLogout={handleLogout} />} />
              <Route path="/deliveries/:id" element={<DeliveryDetail user={user} onLogout={handleLogout} />} />
              <Route path="/pallet-monitoring" element={<PalletMonitoring user={user} onLogout={handleLogout} />} />
            </>
          )}
          
          {user && user.role === 'Driver' && (
            <>
              <Route path="/" element={<DriverView user={user} onLogout={handleLogout} />} />
              <Route path="/deliveries/:id" element={<DeliveryDetail user={user} onLogout={handleLogout} />} />
              <Route path="/pallet-monitoring" element={<PalletMonitoring user={user} onLogout={handleLogout} />} />
            </>
          )}
          
          <Route path="*" element={<Navigate to={user ? safeLandingPath(user) : "/login"} replace />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
